'use memo';

import { useEffect, useState } from 'react';
import { useSelectedContract, useSelectedCampaign } from 'hooks';
import { MenuTitles, Pages } from 'constants/menu';
import { ContractSubPage } from '../ContractSubPage';
import TerminateContractAction from '../TerminateContractAction';
import { ErrorModal } from 'helpers/ErrorModal';
import { isDirtyEqual } from 'utils/compares';
import { getContractInformationSubPages } from './ContractInformationSubPages';
import { SubPagePanel } from 'components/panel/SubPagePanel';
import {
  AgxAlerts,
  AgxButton,
  AgxSpacer,
  AgxTypography,
  formatCurrency,
} from '@urbanx/agx.ui-components2';
import {
  TaskCategoryTaskType,
  TaskCategoryWorkflowType,
  useGetAutopilotTasksForCampaign,
} from '@urbanx/agx.activities.client';
import {
  CampaignDetailModelState,
  Deposit,
  DepositType,
  EarlyReleaseOfDeposit,
  StaffUpdateContractDepositsRequest,
  ValueType,
  useInvalidateUseStaffGetContractDeposits,
  useStaffGetContractDeposits,
  useStaffUpdateContractDeposits,
} from '@urbanx/agx.campaigns.client';
import { DepositPanel } from './panels/DepositPanel';
import { AddDepositButton } from './modals/AddDepositButton';
import LoadingState from 'components/loadingState/LoadingState';
import { useGetListingByCampaignId } from '@urbanx/agx.rex.client';
import { RexDisconnectedModalFields } from 'pages/campaigns/components/manageCampaign/RexDataUpdateHelper';
import { isEqual } from 'lodash-es';
import { useFormConditionDueDate } from './components/useFormConditionDueDate';
import useRexDisconnectedModal, {
  RexDisconnectedModalChange,
} from 'pages/campaigns/components/manageCampaign/useRexDisconnectedModal';
import {
  getDepositExcessAmount,
  getNswBalanceDepositWithDueDate,
  getTotalDepositAmount,
  normalizeDepositToDollar,
} from './depositCalculations';
import EarlyReleaseModal from './modals/EarlyReleaseModal';
import EarlyReleaseSummary from './EarlyReleaseSummary';

const earlyReleaseBlockingTaskTypes = new Set<TaskCategoryTaskType>([
  TaskCategoryTaskType.DepositExceeds10Percent,
  TaskCategoryTaskType.DepositExceeds10PercentPaymentReview,
  TaskCategoryTaskType.EarlyReleaseOfDepositPaymentReview,
  TaskCategoryTaskType.InitialDeposit,
  TaskCategoryTaskType.BalanceDeposit,
]);

const hasDepositValue = (deposit: Deposit | undefined): deposit is Deposit => {
  if (!deposit) return false;

  if (deposit.depositValueType === ValueType.Dollar) {
    return deposit.amount !== undefined && deposit.amount !== null;
  }

  if (deposit.depositValueType === ValueType.Percentage) {
    return deposit.percent !== undefined && deposit.percent !== null;
  }

  return false;
};

const Deposits = () => {
  const { contract, campaignId } = useSelectedContract();
  const { campaign } = useSelectedCampaign();
  const isNsw = campaign?.state === CampaignDetailModelState.NSW;

  const [isLoaded, setIsLoaded] = useState(false);
  const [, setValidate] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [openEarlyReleaseModal, setOpenEarlyReleaseModal] = useState(false);
  const [submitFormErrors, setSubmitFormErrors] = useState<string[]>([]);
  const invalidateUseStaffGetContractDeposits = useInvalidateUseStaffGetContractDeposits();

  const {
    data: contractDeposits,
    isPending,
    isError,
  } = useStaffGetContractDeposits(
    campaignId || '',
    contract?.contractId || '',
    !!campaignId && !!contract?.contractId
  );

  const prevInitialDeposit = contractDeposits?.deposits?.find(
    (d) => d.depositType === DepositType.Initial
  );
  const prevBalanceDeposit = contractDeposits?.deposits?.find(
    (d) => d.depositType === DepositType.Balance
  );
  const earlyReleaseOfDeposit = contractDeposits?.earlyReleaseOfDeposit;

  const [initialDeposit, setInitialDeposit] = useState<Deposit | undefined>(prevInitialDeposit);

  const [balanceDeposit, setBalanceDeposit] = useState<Deposit | undefined>(prevBalanceDeposit);

  const { modal: rexDisconnectedModal, openWithChanges } = useRexDisconnectedModal();

  useEffect(() => {
    setInitialDeposit(prevInitialDeposit);
    setBalanceDeposit(prevBalanceDeposit);
    setIsLoaded(!isPending);
  }, [isPending, prevInitialDeposit, prevBalanceDeposit]);

  useEffect(() => {
    setValidate(
      !isDirtyEqual(initialDeposit, prevInitialDeposit) ||
        !isDirtyEqual(balanceDeposit, prevBalanceDeposit)
    );
  }, [initialDeposit, balanceDeposit, prevInitialDeposit, prevBalanceDeposit]);

  const totalDeposit = getTotalDepositAmount({
    initialDeposit,
    balanceDeposit,
    salePrice: contract?.salePrice ?? undefined,
  });
  const depositExcessAmount = getDepositExcessAmount({
    totalDepositAmount: totalDeposit,
    salePrice: contract?.salePrice,
  });

  const { staffUpdateContractDeposits, staffUpdateContractDepositsPending } =
    useStaffUpdateContractDeposits({
      onSuccess: () => {},
      onError: () => {},
    });

  const { data: rexListing, isPending: isPendingRexListing } = useGetListingByCampaignId(
    campaign?.campaignId || '',
    campaign?.campaignId !== undefined
  );
  const { data: contractTasks } = useGetAutopilotTasksForCampaign(
    campaignId || '',
    contract?.contractId || '',
    !!campaignId && !!contract?.contractId
  );

  const isFormDirty =
    !isDirtyEqual(initialDeposit, prevInitialDeposit) ||
    !isDirtyEqual(balanceDeposit, prevBalanceDeposit);
  const hasInitialDepositValue = hasDepositValue(initialDeposit);
  const hasBalanceDepositValue = hasDepositValue(balanceDeposit);
  const canAddDeposit =
    !hasInitialDepositValue || (isNsw && (!hasInitialDepositValue || !hasBalanceDepositValue));

  // Calculate due dates using the hook
  const prevInitialDueDate = useFormConditionDueDate(prevInitialDeposit);
  const currentInitialDueDate = useFormConditionDueDate(initialDeposit);
  const prevBalanceDueDate = useFormConditionDueDate(prevBalanceDeposit);
  const currentBalanceDueDate = useFormConditionDueDate(balanceDeposit);

  const hasOutstandingDepositTasks = (contractTasks ?? []).some(
    (task) =>
      task.category?.workflowType === TaskCategoryWorkflowType.IndependentContract &&
      task.isCompleted !== true &&
      earlyReleaseBlockingTaskTypes.has(task.category?.taskType as TaskCategoryTaskType)
  );

  const earlyReleaseActionedBy = (() => {
    const changedByUserId = earlyReleaseOfDeposit?.changeInfo?.changedByUserId;
    return changedByUserId ?? '-';
  })();

  const onSave = async (success: () => void) => {
    if (!contract?.contractId) return;

    if (!rexListing && isPendingRexListing === false) {
      const changes: RexDisconnectedModalChange[] = [];

      if (!isEqual(prevInitialDeposit?.amount, initialDeposit?.amount)) {
        changes.push({
          field: RexDisconnectedModalFields.InitialDeposit,
          value: initialDeposit?.amount,
        });
      }
      if (!isEqual(prevInitialDueDate.dueDateString, currentInitialDueDate.dueDateString)) {
        changes.push({
          field: RexDisconnectedModalFields.InitialDepositDueDate,
          value: currentInitialDueDate.dueDateString,
        });
      }
      if (!isEqual(prevBalanceDeposit?.amount, balanceDeposit?.amount)) {
        changes.push({
          field: RexDisconnectedModalFields.BalanceDeposit,
          value: balanceDeposit?.amount,
        });
      }
      if (!isEqual(prevBalanceDueDate.dueDateString, currentBalanceDueDate.dueDateString)) {
        changes.push({
          field: RexDisconnectedModalFields.BalanceDepositDueDate,
          value: currentBalanceDueDate.dueDateString,
        });
      }

      openWithChanges(changes);
    }

    const balanceDepositForSave = isNsw
      ? getNswBalanceDepositWithDueDate({
          deposit: balanceDeposit,
          dueDateString: currentBalanceDueDate.dueDateString,
        })
      : balanceDeposit;

    const deposits = isNsw
      ? [
          normalizeDepositToDollar(initialDeposit, contract.salePrice ?? undefined),
          normalizeDepositToDollar(balanceDepositForSave, contract.salePrice ?? undefined),
        ]
      : [initialDeposit, balanceDeposit];
    const depositsWithValues = deposits.filter(hasDepositValue);

    const request: StaffUpdateContractDepositsRequest = {
      deposits: depositsWithValues,
    };

    await staffUpdateContractDeposits(
      {
        body: request,
        contractId: contract.contractId,
      },
      {
        onSuccess: async (data) => {
          if (data?.success) {
            success();
            await invalidateUseStaffGetContractDeposits(campaignId ?? '', contract.contractId);
          } else {
            setSubmitFormErrors(data?.errors ?? []);
            setOpenErrorModal(true);
          }
        },
      }
    );
  };

  if (!isLoaded) {
    return <LoadingState />;
  }

  return (
    <ContractSubPage
      pageTitle="Contract Information"
      secondaryActiveMenu={MenuTitles.CONTRACT_INFORMATION}
      currentPage={Pages.DEPOSITS}
      subPages={getContractInformationSubPages({ campaignState: campaign?.state })}
      onSaveChanges={onSave}
      isFormDirty={isFormDirty}
      isSaving={staffUpdateContractDepositsPending}
      isError={isError}
      secondaryActions={<TerminateContractAction />}
    >
      {!isPending ? (
        <>
          <AgxSpacer direction="vertical" fill gap={'2xs'}>
            {depositExcessAmount != null && depositExcessAmount > 0 && (
              <AgxAlerts
                alerts={[
                  {
                    alertType: 'Error',
                    heading: `Deposit exceeds 10% of the sale price by ${formatCurrency(depositExcessAmount, 2)}`,
                    id: 'depositExceedErrorAlert',
                    isDismissed: false,
                    title: '',
                    description: '',
                    solveElementId: '',
                  },
                ]}
                id={'depositExeedsAlert'}
                dismissedAlertIds={[]}
                onValueChanged={() => {}}
              />
            )}
            {!hasInitialDepositValue && !hasBalanceDepositValue && (
              <SubPagePanel expandable={false}>
                <AgxSpacer
                  direction="horizontal"
                  gap="spacing-md"
                  contentAlignment="center"
                  contentJustification="center"
                  fill
                  padding={{ top: 'spacing-8xl', bottom: 'spacing-8xl' }}
                >
                  <AgxTypography id="no-deposits" type="body_ui" size="medium">
                    No deposits added.
                  </AgxTypography>
                </AgxSpacer>
              </SubPagePanel>
            )}
            <AgxSpacer direction="vertical" fill gap={'md'}>
              {hasInitialDepositValue && (
                <DepositPanel
                  id="initial-deposit-panel"
                  isNsw={isNsw}
                  panelTitle="Initial Deposit"
                  deposit={initialDeposit}
                  onChange={(value) => setInitialDeposit(value as Deposit | undefined)}
                />
              )}
              {hasBalanceDepositValue && (
                <DepositPanel
                  id="balance-deposit-panel"
                  isNsw={isNsw}
                  panelTitle="Balance Deposit"
                  deposit={balanceDeposit}
                  onChange={(value) => setBalanceDeposit(value as Deposit | undefined)}
                />
              )}
              {earlyReleaseOfDeposit && (
                <EarlyReleaseSummary
                  earlyRelease={earlyReleaseOfDeposit as EarlyReleaseOfDeposit}
                  actionedBy={earlyReleaseActionedBy}
                />
              )}
            </AgxSpacer>
          </AgxSpacer>
          <AgxSpacer
            direction="horizontal"
            gap="spacing-md"
            contentJustification="start"
            contentAlignment="start"
          >
            {canAddDeposit && (
              <AddDepositButton
                id="initial-deposit"
                label={isNsw ? 'Add Deposit' : 'Add Initial Deposit'}
                depositType={DepositType.Initial}
                onAddDeposits={(initial, balance) => {
                  setInitialDeposit(initial);
                  if (isNsw || balance) {
                    setBalanceDeposit(balance);
                  }
                }}
              />
            )}
            {!isNsw && !hasBalanceDepositValue && (
              <AddDepositButton
                id="balance-deposit"
                label="Add Balance Deposit"
                depositType={DepositType.Balance}
                onAddDeposits={(_, balance) => {
                  if (balance) {
                    setBalanceDeposit(balance);
                  }
                }}
                disabled={!hasInitialDepositValue}
              />
            )}
            {!earlyReleaseOfDeposit && (
              <AgxButton
                id="early-release"
                text="Early Release"
                variant="primary"
                danger
                disabled={hasOutstandingDepositTasks || contractTasks == null}
                onClick={() => setOpenEarlyReleaseModal(true)}
              />
            )}
          </AgxSpacer>
        </>
      ) : undefined}
      {openErrorModal && (
        <ErrorModal errorList={submitFormErrors} setShowModal={setOpenErrorModal} />
      )}
      {openEarlyReleaseModal && (
        <EarlyReleaseModal
          isOpen={openEarlyReleaseModal}
          onClose={() => setOpenEarlyReleaseModal(false)}
        />
      )}
      {rexDisconnectedModal}
    </ContractSubPage>
  );
};

export default Deposits;
